import { useMemo, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Loader2,
  History,
  FileText,
  Phone,
  Mail,
  Users,
  MessageSquare,
  Calendar,
  Send,
  Linkedin,
  UserCheck,
  CalendarCheck,
  Megaphone,
  Plus,
  AlertCircle,
} from "lucide-react";
import React from "react";
import { useAccountCampaignTimeline, type AccountCampaignEvent } from "@/hooks/useAccountCampaigns";
import { useEngagementNotes } from "@/hooks/useEngagementNotes";
import { EngagementNoteRow } from "@/components/accounts/engagement/EngagementNoteRow";
import { AddEngagementNoteDialog } from "@/components/accounts/engagement/AddEngagementNoteDialog";

// ─────────────────────────────────────────────────────────────────────────────
// Engagement tab = EXTERNAL touches with humans outside our team
// (outreach, email, call, meeting, demo, LinkedIn, intro, campaign events).
// For INTERNAL system events (note edits, tier changes, AI verdicts), see
// AccountActivityTab. See docs/ACCOUNT_PAGE_GOLD_SHEET.md for the canonical
// routing table.
// ─────────────────────────────────────────────────────────────────────────────

const EVENT_CONFIG: Record<string, { icon: React.ReactNode; color: string }> = {
  campaign_enrollment: { icon: <Megaphone className="h-3.5 w-3.5" />, color: "border-blue-500 text-blue-600" },
  outreach_sent: { icon: <Send className="h-3.5 w-3.5" />, color: "border-indigo-500 text-indigo-600" },
  outreach_replied: { icon: <MessageSquare className="h-3.5 w-3.5" />, color: "border-green-500 text-green-600" },
  connection_accepted: { icon: <UserCheck className="h-3.5 w-3.5" />, color: "border-emerald-500 text-emerald-600" },
  meeting_booked: { icon: <CalendarCheck className="h-3.5 w-3.5" />, color: "border-purple-500 text-purple-600" },
  pipeline_activity: { icon: <FileText className="h-3.5 w-3.5" />, color: "border-primary text-primary" },
  note: { icon: <FileText className="h-3.5 w-3.5" />, color: "border-primary text-primary" },
  call: { icon: <Phone className="h-3.5 w-3.5" />, color: "border-amber-500 text-amber-600" },
  email: { icon: <Mail className="h-3.5 w-3.5" />, color: "border-sky-500 text-sky-600" },
  meeting: { icon: <Users className="h-3.5 w-3.5" />, color: "border-violet-500 text-violet-600" },
  demo: { icon: <MessageSquare className="h-3.5 w-3.5" />, color: "border-pink-500 text-pink-600" },
};

const PLATFORM_ICONS: Record<string, React.ReactNode> = {
  linkedin: <Linkedin className="h-3 w-3" />,
  email: <Mail className="h-3 w-3" />,
  emailbison: <Mail className="h-3 w-3" />,
};

interface AccountEngagementTabProps {
  accountId: string;
  accountName?: string | null;
}

function formatDate(dateStr: string) {
  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export function AccountEngagementTab({ accountId, accountName }: AccountEngagementTabProps) {
  const { data: events = [], isLoading: eventsLoading } = useAccountCampaignTimeline(accountId);
  const { data: repNotes = [], isLoading: notesLoading } = useEngagementNotes(accountId);
  const [addOpen, setAddOpen] = useState(false);

  const proposedCount = useMemo(() => repNotes.filter((n) => n.is_proposed).length, [repNotes]);
  const confirmedNotes = useMemo(() => repNotes.filter((n) => !n.is_proposed), [repNotes]);
  const proposedNotes = useMemo(() => repNotes.filter((n) => n.is_proposed), [repNotes]);
  const engagementCount = events.length + repNotes.length;

  if (eventsLoading || notesLoading) {
    return (
      <div className="flex items-center justify-center h-[300px]">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const hasAnything = engagementCount > 0;

  return (
    <div className="p-4 space-y-5">
      {/* Header + CTA */}
      <div className="flex items-center gap-3 flex-wrap">
        <h3 className="text-sm font-semibold">Engagement</h3>
        <Badge variant="outline" className="text-[11px]">
          {engagementCount} {engagementCount === 1 ? "touch" : "touches"}
        </Badge>
        <span className="text-xs text-muted-foreground">
          External touches with humans outside our team. Internal system events live on the Activity tab.
        </span>
        <div className="ml-auto">
          <Button size="sm" onClick={() => setAddOpen(true)}>
            <Plus className="h-3.5 w-3.5 mr-1" />
            Log engagement
          </Button>
        </div>
      </div>

      {/* Review banner for proposed (migrated) notes */}
      {proposedCount > 0 && (
        <div className="rounded-md border border-amber-300 bg-amber-50/60 px-3 py-2 text-sm flex items-center gap-2">
          <AlertCircle className="h-4 w-4 text-amber-600 shrink-0" />
          <span>
            <span className="font-semibold">{proposedCount}</span>{" "}
            migrated {proposedCount === 1 ? "note" : "notes"} awaiting review. Check the proposed type,
            contact, and outcome, then confirm (or edit) each row.
          </span>
        </div>
      )}

      {/* Rep engagement notes */}
      <section className="space-y-2">
        <div className="flex items-center gap-2">
          <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
            Rep notes
          </h4>
          <Badge variant="outline" className="text-[10px]">{repNotes.length}</Badge>
        </div>
        {repNotes.length === 0 ? (
          <p className="text-sm text-muted-foreground italic">
            No rep-logged engagements yet. Use "Log engagement" above to capture an outreach, call, meeting, or demo.
          </p>
        ) : (
          <div className="space-y-2">
            {proposedNotes.map((n) => (
              <EngagementNoteRow key={n.id} note={n} />
            ))}
            {confirmedNotes.map((n) => (
              <EngagementNoteRow key={n.id} note={n} />
            ))}
          </div>
        )}
      </section>

      {/* Campaign / outreach timeline */}
      <section className="space-y-2">
        <div className="flex items-center gap-2">
          <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
            System timeline
          </h4>
          <Badge variant="outline" className="text-[10px]">{events.length}</Badge>
        </div>
        {events.length === 0 ? (
          <p className="text-sm text-muted-foreground italic">
            No campaign enrollments, outreach sends, or pipeline activities yet.
          </p>
        ) : (
          <>
            <div className="flex items-center gap-3 flex-wrap text-xs text-muted-foreground">
              <span>{events.filter((e) => e.event_type === "campaign_enrollment").length} enrollments</span>
              <span>{events.filter((e) => e.event_type === "outreach_sent").length} sends</span>
              <span>{events.filter((e) => e.event_type === "outreach_replied").length} replies</span>
              <span>{events.filter((e) => e.event_type === "meeting_booked").length} meetings</span>
              <span>{events.filter((e) => e.event_type === "pipeline_activity").length} pipeline</span>
            </div>
            <div className="relative border-l-2 border-muted ml-4 space-y-4 pt-2">
              {events.map((event: AccountCampaignEvent) => {
                const cfg =
                  EVENT_CONFIG[event.status || event.event_type] ||
                  EVENT_CONFIG[event.event_type] ||
                  EVENT_CONFIG.pipeline_activity;
                return (
                  <div key={event.id} className="relative pl-8">
                    <div
                      className={`absolute -left-[13px] top-0 w-6 h-6 rounded-full bg-background border-2 flex items-center justify-center ${cfg.color}`}
                    >
                      {cfg.icon}
                    </div>
                    <div className="space-y-0.5">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-sm">{event.title}</span>
                        {event.platform && (
                          <span className="flex items-center gap-0.5 text-muted-foreground">
                            {PLATFORM_ICONS[event.platform]}
                          </span>
                        )}
                        {event.campaign_name && (
                          <Badge variant="outline" className="text-[10px] h-4 px-1">
                            {event.campaign_name}
                          </Badge>
                        )}
                        {event.status && event.status !== event.event_type && (
                          <Badge variant="secondary" className="text-[10px] h-4 px-1">
                            {event.status.replace(/_/g, " ")}
                          </Badge>
                        )}
                      </div>
                      {event.contact_name && (
                        <p className="text-xs text-muted-foreground">{event.contact_name}</p>
                      )}
                      {event.detail && (
                        <p className="text-xs text-muted-foreground line-clamp-2">{event.detail}</p>
                      )}
                      <div className="flex items-center gap-1 text-[10px] text-muted-foreground">
                        <Calendar className="h-3 w-3" />
                        {formatDate(event.event_date)}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </section>

      {!hasAnything && (
        <div className="p-8 text-center">
          <History className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
          <p className="text-sm text-muted-foreground">
            No engagement yet. Log the first touch above.
          </p>
        </div>
      )}

      <AddEngagementNoteDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        accountId={accountId}
        accountName={accountName ?? null}
      />
    </div>
  );
}
